// src/components/assessment/AssessmentHistory.jsx
import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Grid, Chip,
    TextField, MenuItem, InputAdornment, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TablePagination, Button, Card, CardContent,
    Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    GetApp as DownloadIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/formatters';
import { getAssessments, deleteAssessment } from '../../services/assessmentService';

const AssessmentHistory = () => {
    const navigate = useNavigate();
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtering and pagination states
    const [filteredAssessments, setFilteredAssessments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [domainFilter, setDomainFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [riskLevelFilter, setRiskLevelFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [assessmentToDelete, setAssessmentToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        fetchAssessments();
    }, []);

    // Apply filters whenever filter states change
    useEffect(() => {
        applyFilters();
    }, [assessments, searchTerm, domainFilter, dateFilter, riskLevelFilter]);

    const fetchAssessments = async () => {
        try {
            setLoading(true);
            const data = await getAssessments();
            setAssessments(data);
            setFilteredAssessments(data);
        } catch (err) {
            setError('Failed to load assessment history. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...assessments];

        // Apply search term filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (assessment) =>
                    assessment.title.toLowerCase().includes(term) ||
                    assessment.domain.toLowerCase().includes(term)
            );
        }

        // Apply domain filter
        if (domainFilter !== 'all') {
            filtered = filtered.filter(
                (assessment) => assessment.domain === domainFilter
            );
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const cutoffDate = new Date();

            if (dateFilter === 'last30days') {
                cutoffDate.setDate(now.getDate() - 30);
            } else if (dateFilter === 'last90days') {
                cutoffDate.setDate(now.getDate() - 90);
            } else if (dateFilter === 'last6months') {
                cutoffDate.setMonth(now.getMonth() - 6);
            } else if (dateFilter === 'lastyear') {
                cutoffDate.setFullYear(now.getFullYear() - 1);
            }

            filtered = filtered.filter(
                (assessment) => new Date(assessment.created_at) >= cutoffDate
            );
        }

        // Apply risk level filter
        if (riskLevelFilter !== 'all') {
            let minScore, maxScore;

            if (riskLevelFilter === 'low') {
                minScore = 0;
                maxScore = 3.9;
            } else if (riskLevelFilter === 'medium') {
                minScore = 4;
                maxScore = 6.9;
            } else if (riskLevelFilter === 'high') {
                minScore = 7;
                maxScore = 10;
            }

            filtered = filtered.filter(
                (assessment) =>
                    assessment.risk_score >= minScore &&
                    assessment.risk_score <= maxScore
            );
        }

        setFilteredAssessments(filtered);
        setPage(0); // Reset to first page when filters change
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleDomainFilterChange = (event) => {
        setDomainFilter(event.target.value);
    };

    const handleDateFilterChange = (event) => {
        setDateFilter(event.target.value);
    };

    const handleRiskLevelFilterChange = (event) => {
        setRiskLevelFilter(event.target.value);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setDomainFilter('all');
        setDateFilter('all');
        setRiskLevelFilter('all');
    };

    const handleDeleteClick = (assessment) => {
        setAssessmentToDelete(assessment);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!assessmentToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteAssessment(assessmentToDelete.id);

            // Remove from state
            const updatedAssessments = assessments.filter(
                (a) => a.id !== assessmentToDelete.id
            );
            setAssessments(updatedAssessments);

            // Close dialog
            setDeleteDialogOpen(false);
            setAssessmentToDelete(null);
        } catch (err) {
            console.error('Error deleting assessment:', err);
            setError('Failed to delete assessment. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAssessmentToDelete(null);
    };

    const handleViewAssessment = (id) => {
        navigate(`/assessment/${id}`);
    };

    // Function to determine risk level text and color
    const getRiskLevelInfo = (score) => {
        if (score >= 7) {
            return { level: 'High', color: '#d32f2f' };
        } else if (score >= 4) {
            return { level: 'Medium', color: '#f57c00' };
        } else {
            return { level: 'Low', color: '#388e3c' };
        }
    };

    // Format domain name for display
    const formatDomainName = (domain) => {
        return domain.charAt(0).toUpperCase() + domain.slice(1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Assessment History
            </Typography>

            {error && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography>{error}</Typography>
                </Paper>
            )}

            {/* Filters and Search */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setSearchTerm('')} size="small">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            placeholder="Search by title or domain"
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                select
                                label="Domain"
                                value={domainFilter}
                                onChange={handleDomainFilterChange}
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="all">All Domains</MenuItem>
                                <MenuItem value="financial">Financial</MenuItem>
                                <MenuItem value="cybersecurity">Cybersecurity</MenuItem>
                                <MenuItem value="health">Health & Safety</MenuItem>
                                <MenuItem value="operational">Operational</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="Date Range"
                                value={dateFilter}
                                onChange={handleDateFilterChange}
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="all">All Time</MenuItem>
                                <MenuItem value="last30days">Last 30 Days</MenuItem>
                                <MenuItem value="last90days">Last 90 Days</MenuItem>
                                <MenuItem value="last6months">Last 6 Months</MenuItem>
                                <MenuItem value="lastyear">Last Year</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="Risk Level"
                                value={riskLevelFilter}
                                onChange={handleRiskLevelFilterChange}
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="all">All Levels</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                            </TextField>

                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleResetFilters}
                                size="small"
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" color="text.secondary">
                    {filteredAssessments.length} {filteredAssessments.length === 1 ? 'result' : 'results'} found
                </Typography>

                <Button
                    component={Link}
                    to="/new-assessment"
                    variant="contained"
                    color="primary"
                >
                    New Assessment
                </Button>
            </Box>

            {/* Assessment Table */}
            {filteredAssessments.length > 0 ? (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Domain</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Risk Score</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAssessments
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((assessment) => {
                                        const riskInfo = getRiskLevelInfo(assessment.risk_score);

                                        return (
                                            <TableRow key={assessment.id} hover>
                                                <TableCell component="th" scope="row">
                                                    {assessment.title}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={formatDomainName(assessment.domain)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(assessment.created_at)}</TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            backgroundColor: `${riskInfo.color}20`,
                                                            color: riskInfo.color,
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {assessment.risk_score.toFixed(1)} - {riskInfo.level}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleViewAssessment(assessment.id)}
                                                        title="View Details"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteClick(assessment)}
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredAssessments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            ) : (
                <Card variant="outlined" sx={{ textAlign: 'center', py: 4 }}>
                    <CardContent>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No assessments found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {assessments.length > 0
                                ? 'Try adjusting your search filters to find assessments.'
                                : 'You haven\'t created any risk assessments yet.'}
                        </Typography>

                        <Button
                            component={Link}
                            to="/new-assessment"
                            variant="contained"
                            color="primary"
                        >
                            Create Your First Assessment
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>Delete Assessment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the assessment "{assessmentToDelete?.title}"?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AssessmentHistory;